import React from 'react';
import { Button } from '../../../../';
import { Form, Alert, Input, Space } from '../../../../../components';
import { DownloadOutlined, CopyOutlined, LoadingOutlined } from '@ant-design/icons';
import { NameField, VSphereVersionField } from '../../cloudAccountFields';
import {
    getApplianceDownloadUrl,
    getTokenLabel,
    getOpenShiftServicePolicyYAML,
} from '../../utils/applianceDownloadHelpers';
import { getDownloadableDocumentation } from '../../utils/cloudAccountHelpers';
import { isVSphere, isOpenShift, isOpenStack, isHPEVME, isNC2, isHyperV } from '../../../../../constants/cloudTypes';
import './DownloadableCloudAccount.scss';

const { TextArea } = Input;

/**
 * Downloadable Cloud Account - Add Mode
 * Focused component for adding downloadable cloud accounts
 * (vSphere, OpenShift, OpenStack, NC2, HPE VME, Hyper-V)
 */
export const DownloadableCloudAccountAdd = ({
    cloudType,
    formData,
    errors,
    onChange,
    onGenerateToken,
    generatingToken,
    generatedToken,
    onCopyToken,
    notifySuccess,
    notifyError,
}) => {
    const getTokenInstructionsMessage = () => {
        if (isVSphere(cloudType)) {
            return {
                message: 'Token-Based Setup Required',
                description:
                    'To complete the setup of your vSphere target cloud, you will need to generate and apply an authentication token. Download the appliance, then generate the token to complete the setup.',
            };
        }
        if (isOpenShift(cloudType)) {
            return {
                message: 'OpenShift Policy Configuration',
                description:
                    'Before proceeding, ensure you have configured the necessary policies for OpenShift. Download the appliance and generate the customization script to complete the setup.',
            };
        }
        if (isOpenStack(cloudType)) {
            return {
                message: 'OpenStack Policy Configuration',
                description:
                    'Before proceeding, ensure you have configured the necessary policies for OpenStack. Download the appliance and generate the customization script to complete the setup.',
            };
        }
        if (isHPEVME(cloudType)) {
            return {
                message: 'HPE VM Essentials Setup',
                description:
                    'Download the migration appliance and generate the customization script. Apply the customization script during the appliance deployment process.',
            };
        }
        if (isNC2(cloudType)) {
            return {
                message: 'Nutanix NC2 Setup',
                description:
                    'Download the migration appliance and generate the customization script to complete your Nutanix NC2 setup.',
            };
        }
        if (isHyperV(cloudType)) {
            return {
                message: 'Hyper-V Setup',
                description:
                    'Download the migration appliance and generate the customization script to complete your Hyper-V setup.',
            };
        }
        return {
            message: 'Token-Based Authentication',
            description:
                'This cloud type requires token-based authentication. Download the appliance and generate the required token/script to proceed.',
        };
    };

    const instructions = getTokenInstructionsMessage();
    const applianceUrl = getApplianceDownloadUrl(cloudType);
    const tokenLabel = getTokenLabel(cloudType);
    const servicePolicyYAML = isOpenShift(cloudType) ? getOpenShiftServicePolicyYAML() : null;
    const documentation = getDownloadableDocumentation(cloudType);

    // Handler to copy service policy YAML to clipboard
    const handleCopyServicePolicy = async () => {
        try {
            await navigator.clipboard.writeText(servicePolicyYAML);
            notifySuccess('Service Policy YAML copied to clipboard!');
        } catch (err) {
            notifyError('Failed to copy to clipboard');
        }
    };

    return (
        <>
            {/* vSphere Version - only for vSphere */}
            {isVSphere(cloudType) && (
                <VSphereVersionField
                    value={formData.vsphere_version}
                    onChange={onChange('vsphere_version')}
                    error={errors.vsphere_version}
                />
            )}

            <NameField
                value={formData.name}
                onChange={onChange('name')}
                error={errors.name}
                required={true}
                extra={
                    isHyperV(cloudType)
                        ? 'Only alphanumerics, periods, spaces, dashes and underscores are allowed.'
                        : undefined
                }
            />

            {/* Token Generation Instructions */}
            <Alert
                title={instructions.message}
                description={instructions.description}
                type="info"
                showIcon
                className="downloadable-instructions-alert"
            />

            {/* Download Appliance Section */}
            <Form.Item label="Download Migration Appliance">
                <Space orientation="vertical" className="downloadable-full-width-space">
                    <Button type="primary" icon={<DownloadOutlined />} href={applianceUrl} download>
                        Download
                    </Button>
                    {documentation && (
                        <div className="downloadable-documentation-section">
                            <span className="downloadable-documentation-label">{documentation.label}</span>
                            <a
                                href={documentation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="downloadable-documentation-link"
                            >
                                {documentation.linkText}
                            </a>
                        </div>
                    )}
                </Space>
            </Form.Item>

            {/* Generate Token/ConfigMap Section */}
            <Form.Item
                required={true}
                validateStatus={errors.token ? 'error' : ''}
                help={errors.token}
                label={
                    isOpenShift(cloudType)
                        ? 'Copy/Paste the ConfigMap YAML for Instance Configuration'
                        : `Generate ${tokenLabel}`
                }
            >
                <Space orientation="vertical" className="downloadable-full-width-space">
                    <Button
                        type="primary"
                        onClick={onGenerateToken}
                        disabled={generatingToken}
                        icon={generatingToken ? <LoadingOutlined /> : null}
                    >
                        {generatingToken ? `Generating ${tokenLabel}...` : `Generate ${tokenLabel}`}
                    </Button>

                    {generatedToken && (
                        <>
                            <TextArea value={generatedToken} rows={8} readOnly className="downloadable-mono-textarea" />
                            <Button type="default" icon={<CopyOutlined />} onClick={onCopyToken}>
                                Copy to Clipboard
                            </Button>
                        </>
                    )}
                </Space>
            </Form.Item>

            {/* OpenShift Service Policy Section */}
            {isOpenShift(cloudType) && (
                <Form.Item label="Copy/Paste the Service Policy YAML for VM-based enabled Migration Appliance Ingress Point">
                    <Space orientation="vertical" className="downloadable-full-width-space">
                        <TextArea value={servicePolicyYAML} rows={14} readOnly className="downloadable-mono-textarea" />
                        <Button type="default" icon={<CopyOutlined />} onClick={handleCopyServicePolicy}>
                            Copy to Clipboard
                        </Button>
                    </Space>
                </Form.Item>
            )}
        </>
    );
};
